import React, { useState, useEffect, useRef } from "react";
import api from "@/services/api";
import { useMyContext } from "@/context/ContextApi";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { jwtDecode } from "jwt-decode";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, ShieldOff, Camera } from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";

const UserProfile = () => {
  const { t } = useTranslation();
  const { fetchUser, currentUser, token } = useMyContext();
  const [loginSession, setLoginSession] = useState(null);
  const [is2faEnabled, setIs2faEnabled] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [disabledLoader, setDisbledLoader] = useState(false);
  const [twofaCodeLoader, settwofaCodeLoader] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const fileInputRef = useRef(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: { username: currentUser?.username, email: currentUser?.email, password: "" },
    mode: "onTouched",
  });

  useEffect(() => {
    setPageLoader(true);
    const fetch2FAStatus = async () => {
      try {
        const response = await api.get(`/auth/user/2fa-status`);
        setIs2faEnabled(response.data);
      } catch {
        toast.error(t("userProfile_2fa_fetchError"));
      } finally {
        setPageLoader(false);
      }
    };
    fetch2FAStatus();
  }, []);

  const enable2FA = async () => {
    setDisbledLoader(true);
    try {
      const response = await api.post(`/auth/enable-2fa`);
      setQrCodeUrl(response.data);
      setStep(2);
    } catch {
      toast.error(t("userProfile_2fa_enableError"));
    } finally {
      setDisbledLoader(false);
    }
  };

  const disable2FA = async () => {
    setDisbledLoader(true);
    try {
      await api.post(`/auth/disable-2fa`);
      setIs2faEnabled(false);
      setQrCodeUrl("");
    } catch {
      toast.error(t("userProfile_2fa_disableError"));
    } finally {
      setDisbledLoader(false);
    }
  };

  const verify2FA = async () => {
    if (!code || code.trim().length === 0) return toast.error(t("userProfile_2fa_verifyError"));
    settwofaCodeLoader(true);
    try {
      await api.post(`/auth/verify-2fa`, { code });
      toast.success(t("userProfile_2fa_verifySuccess"));
      setIs2faEnabled(true);
      setStep(1);
    } catch {
      toast.error(t("userProfile_2fa_invalidCode"));
    } finally {
      settwofaCodeLoader(false);
    }
  };

  const handleUpdateCredential = async (data) => {
    try {
      setLoading(true);
      await api.put("/users/me", { username: data.username, email: data.email, password: data.password });
      fetchUser();
      toast.success(t("userProfile_saveSuccess"));
    } catch {
      toast.error(t("userProfile_saveError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.id) {
      setValue("username", currentUser.username);
      setValue("email", currentUser.email);
    }
  }, [currentUser, setValue]);

  useEffect(() => {
    if (token) {
      const decoded = jwtDecode(token);
      setLoginSession(moment.unix(decoded.iat).format("dddd, D MMMM YYYY, h:mm A"));
    }
  }, [token]);

  const reloadAvatar = async () => {
    try {
      const res = await api.get("/users/me/avatar", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setAvatarUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch { /* no avatar set */ }
  };

  useEffect(() => {
    reloadAvatar();
    return () => { if (avatarUrl) URL.revokeObjectURL(avatarUrl); };
  }, []);

  const uploadAvatar = (file) => {
    if (file.size > 1 * 1024 * 1024) throw new Error(t("userProfile_avatarErrorSize"));
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) throw new Error(t("userProfile_avatarErrorFormat"));
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result.split(",")[1];
          await api.post("/users/me/avatar", { avatarBase64: base64 });
          resolve();
        } catch (e) { reject(e); }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  if (pageLoader) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] gap-3">
        <Loader2 size={24} className="animate-spin text-violet-400" />
        <span className="text-sm text-zinc-500">{t("userProfile_loading")}</span>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] px-4 py-12">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header */}
        <div className="mb-2">
          <span className="text-xs font-mono uppercase tracking-widest text-violet-400">{t("userProfile_label")}</span>
          <h1 className="text-2xl font-bold text-white mt-1">{t("userProfile_title")}</h1>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">

          {/* Left card — profile */}
          <div className="rounded-xl border border-white/8 bg-white/2 p-6 space-y-6">

            {/* Avatar */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative group">
                <Avatar
                  className="w-20 h-20 cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <AvatarImage src={avatarUrl ?? undefined} />
                  <AvatarFallback className="bg-violet-500/20 text-violet-300 text-2xl font-bold">
                    {currentUser?.username?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute inset-0 rounded-full flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Camera size={18} className="text-white" />
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    await uploadAvatar(file);
                    await reloadAvatar();
                    e.target.value = "";
                  } catch (err) {
                    toast.error(err.message);
                  }
                }}
              />
              <div className="text-center">
                <p className="font-semibold text-white">{currentUser?.username}</p>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${
                    currentUser?.role === "ADMIN"
                      ? "text-violet-400 bg-violet-400/10 border-violet-400/20"
                      : "text-zinc-400 bg-zinc-400/10 border-zinc-400/20"
                  }`}>
                    {currentUser?.role}
                  </span>
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(handleUpdateCredential)} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("userProfile_usernameLabel")}</Label>
                <Input
                  {...register("username")}
                  className="bg-white/5 border-white/10 text-white focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("userProfile_emailLabel")}</Label>
                <Input
                  {...register("email")}
                  // readOnly
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("userProfile_passwordLabel")}</Label>
                <Input
                  type="password"
                  placeholder={t("userProfile_passwordPlaceholder")}
                  {...register("password")}
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-violet-600 hover:bg-violet-500 text-white font-semibold"
              >
                {loading ? t("userProfile_loading") : t("userProfile_saveButton")}
              </Button>
            </form>

            {/* Last login */}
            {loginSession && (
              <div className="pt-4 border-t border-white/8">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Last login</p>
                <p className="text-xs text-zinc-400">{loginSession}</p>
              </div>
            )}
          </div>

          {/* Right card — 2FA */}
          <div className="rounded-xl border border-white/8 bg-white/2 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-white">{t("userProfile_2fa_title")}</h2>
                <p className="text-xs text-zinc-500 mt-0.5">{t("userProfile_2fa_description")}</p>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                is2faEnabled
                  ? "text-green-400 bg-green-400/10 border-green-400/20"
                  : "text-zinc-500 bg-zinc-500/10 border-zinc-500/20"
              }`}>
                {is2faEnabled ? <ShieldCheck size={12} /> : <ShieldOff size={12} />}
                {is2faEnabled ? t("userProfile_2fa_active") : t("userProfile_2fa_inactive")}
              </span>
            </div>

            <Button
              onClick={is2faEnabled ? disable2FA : enable2FA}
              disabled={disabledLoader}
              variant={is2faEnabled ? "destructive" : "default"}
              className={!is2faEnabled ? "bg-violet-600 hover:bg-violet-500 text-white font-semibold" : ""}
            >
              {disabledLoader ? t("userProfile_2fa_loading") : is2faEnabled ? t("userProfile_2fa_disable") : t("userProfile_2fa_enable")}
            </Button>

            {step === 2 && (
              <Accordion type="single" collapsible defaultValue="qr">
                <AccordionItem value="qr" className="border-white/8">
                  <AccordionTrigger className="text-sm text-zinc-300 hover:text-white">
                    {t("userProfile_2fa_scanQR")}
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <img
                      src={qrCodeUrl}
                      alt="QR Code"
                      className="rounded-lg border border-white/8 mx-auto"
                    />
                    <div className="flex gap-2">
                      <Input
                        placeholder={t("userProfile_2fa_codePlaceholder")}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 font-mono tracking-widest text-center focus-visible:ring-violet-500/50"
                      />
                      <Button
                        onClick={verify2FA}
                        className="bg-violet-600 hover:bg-violet-500 text-white shrink-0"
                      >
                        {twofaCodeLoader ? "…" : t("userProfile_2fa_verify")}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
